import { useCallback, useContext, useEffect, useMemo } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import measurements, { getMeasurementSync } from '@kentik/ui-shared/query/measurements';
import { useQueries, useQuery } from '@tanstack/react-query';
import { transformAndFilterReconMeasurements } from '@kentik/ui-shared/dictionary/v2/compilers/transformAndFilterReconMeasurements';
import useArrayMemo from 'core/util/hooks/useArrayMemo';
import transformDeviceSubtypeMeasurements from '@kentik/ui-shared/dictionary/v2/compilers/deviceSubtypeMeasurements';
import transformEventsAppProtocolMeasurements from '@kentik/ui-shared/dictionary/v2/compilers/eventsAppProtocolMeasurements';
import { eventsAppProtocolsQuery } from 'app/stores/dictionary/raw/eventsAppProtocols';
import { AppProtocolsDictionary, DeviceSubtypes, EventsAppProtocols } from '@kentik/ui-shared/dictionary/types';
import { useDeviceSubtypesQuery } from 'app/stores/dictionary/raw/deviceSubtypes';
import { reconMeasurementsQuery } from 'app/stores/dictionary/raw/reconMeasurements';
import { appProtocolsQuery } from 'app/stores/dictionary/raw/appProtocols';
import { augmentMeasurementsWithDeviceTags } from '@kentik/ui-shared/dictionary/v2/compilers/deviceTagMeasurements';
import { augmentTrafficMeasurementsWithCustomDimensions } from '@kentik/ui-shared/dictionary/v2/compilers/customDimensionMeasurements';
import { deviceTagsQuery } from 'app/stores/dictionary/raw/deviceTags';
import { customDimensionsQuery } from 'app/stores/dictionary/raw/customDimensions';
import { transformAppProtocols } from '@kentik/ui-shared/dictionary/v2/compilers/appProtocolMeasurements';
import type { LookupStore } from 'app/stores/$lookups';

interface DictionaryMeasurementsOptions {
  // If provided, only fetch measurements with this name prefix
  measurementName?: string | null;
  // If provided, filter measurements/dimensions/metrics by last seen time (in seconds)
  lookback?: number;
}

export interface DictionaryMeasurementsState {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

export type MeasurementHashEntry = Pick<Measurement, 'displayName' | 'storage'>;
export type MeasurementHash = Record<string, MeasurementHashEntry>;

interface DictionaryMeasurementsResult extends DictionaryMeasurementsState {
  measurements: Measurement[];
  measurementHash: MeasurementHash;
  appProtocols: AppProtocolsDictionary | undefined;
}

/**
 * Fetches dictionary measurements and fuses them with static measurements
 */
export const useDictionaryMeasurements = ({
  measurementName,
  lookback
}: DictionaryMeasurementsOptions = {}): DictionaryMeasurementsResult => {
  const deviceSubtypesQuery = useDeviceSubtypesQuery();

  const selectNms = useCallback(
    (data: Measurement[]) => transformAndFilterReconMeasurements(data, { lookback }),
    [lookback]
  );

  const selectSubtypes = useCallback(
    (data: DeviceSubtypes) => transformDeviceSubtypeMeasurements(deviceSubtypesQuery.select(data)),
    [deviceSubtypesQuery]
  );

  const selectEvents = useCallback((data: EventsAppProtocols) => transformEventsAppProtocolMeasurements(data), []);

  const deviceTagsResult = useQuery(deviceTagsQuery);
  const customDimensionsResult = useQuery(customDimensionsQuery);

  const { $lookups } = useContext(MobXProviderContext) as { $lookups?: LookupStore };
  useEffect(() => {
    if (deviceTagsResult.data && $lookups) {
      $lookups.registerDeviceTagHandlers(deviceTagsResult.data);
    }
  }, [deviceTagsResult.data, $lookups]);

  const results = useQueries({
    queries: [
      {
        ...reconMeasurementsQuery,
        select: selectNms,
        enabled: !measurementName || measurementName.startsWith('/metrics')
      },
      {
        ...deviceSubtypesQuery,
        select: selectSubtypes,
        enabled:
          !measurementName ||
          measurementName === '/traffic' ||
          ['/traffic/vendors', '/traffic/cloud', '/traffic/container-networking'].some((prefix) =>
            measurementName.startsWith(prefix)
          )
      },
      {
        ...eventsAppProtocolsQuery,
        select: selectEvents,
        enabled: !measurementName || measurementName.startsWith('/events')
      },
      {
        ...appProtocolsQuery,
        select: transformAppProtocols,
        enabled: !measurementName || measurementName === '/traffic'
      }
    ]
  });

  const [reconResult, subtypesResult, eventsResult, appProtocolsResult] = results;

  const baseMeasurements = [reconResult, subtypesResult, eventsResult].reduce(
    (acc, result) => (result.data ? [...acc, ...(result.data as Measurement[])] : acc),
    measurements
  );

  const withDeviceTags = augmentMeasurementsWithDeviceTags(baseMeasurements, deviceTagsResult.data ?? []);
  const allMeasurements = useArrayMemo(
    augmentTrafficMeasurementsWithCustomDimensions(withDeviceTags, customDimensionsResult.data ?? [])
  );

  const appProtocolData = appProtocolsResult.data as ReturnType<typeof transformAppProtocols>;

  const enrichedMeasurements = useMemo(() => {
    if (!appProtocolData) {
      return allMeasurements;
    }

    const { dimensions: appProtocolDims, metrics: appProtocolMetrics } = appProtocolData;
    if (Object.keys(appProtocolDims).length === 0 && Object.keys(appProtocolMetrics).length === 0) {
      return allMeasurements;
    }

    return allMeasurements.map((m) => {
      if (m.measurement !== '/traffic') {
        return m;
      }

      return {
        ...m,
        storage: {
          ...m.storage,
          Dimensions: { ...m.storage.Dimensions, ...appProtocolDims },
          Metrics: { ...m.storage.Metrics, ...appProtocolMetrics }
        }
      };
    });
  }, [allMeasurements, appProtocolData]);

  /*
   * TODO: I'm propagating this through in order to make it easier to grab things by measurement name. However my actual
   * use case was only partially solved by this and I still have to iterate over keys so I'm not convinced that this is
   *  necessary and all that code related to measurementHash could be removed as long as measurementTransforms.ts is
   *  updated to deal with Measurement[].
   */
  const measurementHash = useMemo(
    () =>
      enrichedMeasurements.reduce<MeasurementHash>((acc, { measurement, displayName, storage }) => {
        acc[measurement] = { displayName, storage };
        return acc;
      }, {}),
    [enrichedMeasurements]
  );

  return {
    isLoading:
      results.some((result) => result.isInitialLoading) ||
      deviceTagsResult.isInitialLoading ||
      customDimensionsResult.isInitialLoading,
    isError: results.some((result) => result.isError) || deviceTagsResult.isError || customDimensionsResult.isError,
    isSuccess:
      results.every((result) => result.isSuccess) && deviceTagsResult.isSuccess && customDimensionsResult.isSuccess,
    measurements: enrichedMeasurements,
    measurementHash,
    appProtocols: appProtocolData?.app_protocols
  };
};

type DictionaryMeasurementOptions = Omit<DictionaryMeasurementsOptions, 'measurementName'>;

interface DictionaryMeasurementResult extends DictionaryMeasurementsState {
  measurement: Measurement | null;
}

export const useDictionaryMeasurement = (
  measurementName: string | null,
  options?: DictionaryMeasurementOptions
): DictionaryMeasurementResult => {
  const { measurements: allMeasurements = [], ...state } = useDictionaryMeasurements({ measurementName, ...options });
  return { measurement: measurementName ? getMeasurementSync(measurementName, allMeasurements) : null, ...state };
};
